'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';

interface Project {
  title: string;
  description: string;
  mockupImage: string;
  logo: string;
  link?: string;
  slug?: string;
}

interface ProjectNextProps {
  projects?: Project[];
  allProjects?: Project[];
  currentSlug?: string;
}

const fontWeights = {
  lato: { extrabold: 800 },
  asap: { regular: 400 },
};

const ProjectNext = ({ projects = [], allProjects = [], currentSlug }: ProjectNextProps) => {
  // Helper to normalize slugs for reliable comparison
  const normalize = (s?: string) => s?.replace(/^\//, '').replace(/^portfolio\//, '').replace(/\/$/, '').toLowerCase() || '';
  
  const currentNormalized = normalize(currentSlug);
  const visibleProjects = allProjects.filter(p => normalize(p.slug || p.link) !== currentNormalized);

  // 1. Determine which projects to show
  let displayProjects: Project[] = [];

  // Find current project index in the full list
  const currentIndex = allProjects.findIndex(p => normalize(p.slug || p.link) === currentNormalized);

  if (currentIndex !== -1 && allProjects.length > 1) {
    // Dynamic logic: Get the neighbors in the visible list
    // We use allProjects here because it contains the current one, giving us context of "before" and "after"
    const prevIndex = (currentIndex - 1 + allProjects.length) % allProjects.length;
    const nextIndex = (currentIndex + 1) % allProjects.length;

    const prevProject = allProjects[prevIndex];
    const nextProject = allProjects[nextIndex];

    if (prevProject === nextProject) {
      displayProjects = [prevProject];
    } else {
      displayProjects = [prevProject, nextProject];
    }
  } else if (visibleProjects.length > 0) {
    // Fallback: Just show the first two available visible projects
    displayProjects = visibleProjects.slice(0, 2);
  }

  if (displayProjects.length === 0) {
    return null; // Don't show the section if no other projects exist
  }

  return (
    <section className="bg-white w-full">
      <div className="max-w-[1420px] mx-auto my-3 sm:my-4 md:my-8 lg:my-12 px-4 sm:px-6">
        <div className="grid grid-cols-1 tsm-350:grid-cols-2 gap-2 sm:gap-4 md:gap-8 lg:gap-12 xl:gap-16">
          {displayProjects.map((other, idx) => (
            <Link key={idx} href={other.link || `/portfolio/${other.slug}`} className="group block h-full">
              <div className="bg-[#F8FAFC] rounded-[10px] overflow-hidden transition-all hover:shadow-xl border border-[#e2e8f0] h-full flex flex-col">
                <div className="relative aspect-[16/10] w-full bg-white p-4 sm:p-6 lg:p-8 shrink-0">
                  {other.mockupImage && (
                    <Image
                      src={other.mockupImage}
                      alt={other.title}
                      fill
                      className="object-contain transition-transform p-2 sm:p-4 lg:p-8"
                    />
                  )}
                </div>
                <div className="p-2 sm:p-4 md:p-6 lg:p-8 flex flex-col gap-3 relative flex-grow">
                  <div className="flex flex-col items-start tsm-350:flex-row tsm-350:items-center gap-2 sm:gap-3">
                    {other.logo && (
                      <div className="relative w-6 sm:w-8 md:w-10 lg:w-12 h-6 sm:h-8 md:h-10 lg:h-12 shrink-0">
                        <Image src={other.logo} alt="" fill className="object-contain" />
                      </div>
                    )}
                    <h3 className="text-[16px] sm:text-[20px] md:text-[24px] lg:text-[26px] text-[#09155E] leading-tight"
                      style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.extrabold }}>
                      {other.title}
                    </h3>
                  </div>
                  <p className="text-[#585B6F] text-[12px] sm:text-[16px] lg:text-[17px] leading-[1.5] sm:leading-relaxed"
                    style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}>
                    {other.description}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectNext;
